<template>
  <li
    class="review row c-pb-3 c-md:c-pb-4 c-lg:c-pb-5"
    :class="comment.is_answer ? 'ml-6' : 'c-md:c-mx-0'"
  >
    <!-- photo -->
    <div class="col-2 c-pr-0 c-pl-0">
      <div
        v-if="comment.imageUrl"
        class="c-w-100 c-mb-0 bg-white pattern-2"
      >
        <RemoteImage
          :src="comment.is_answer ? defaultReviewImg : comment.imageUrl"
          img-class="c-w-100 mb-n1"
          :alt="comment.shortName"
          sizes="143px"
        />
      </div>
      <div
        v-else
        class="relative"
      >
        <div class="embed-responsive embed-responsive-1by1 c-mb-0 bg-white pattern-2" />
        <div class="c-mb-0 absolute bottom-0 left-0 c-w-120">
          <BirdSkin class="c-w-100 mb-n1" />
        </div>
      </div>
    </div>
    <!-- review -->
    <div class="col-10 c-pl-4 c-md:c-pl-5 c-md:c-mr-0 flex c-flex-col">
      <div class="shadow block c-py-3 c-px-4 c-rounded-lg comment-bubble relative">
        <div
          class="row c-items-center order-1 c-lg:order-0 text-sm-c"
          :class="registeredUser ? 'mb-1' : 'mb-0 c-md:c-mb-3'"
        >
          <!-- name + date -->
          <div class="flex c-w-100 c-justify-start c-flex-wrap">
            <div
              v-if="comment.verifiedComment"
              v-tooltip="{
                content: t('verified_user_message'),
                theme: 'verifiedUserTooltip',
              }"
              class="absolute z-11 mt-n5 ml-n4"
            >
              <VerifiedUserSvg />
            </div>
            <div class="flex c-flex-row c-items-center">
              <strong class="col-auto order-0">
                {{ comment.is_answer ? `Mixit team -> ${ comment.shortName }` : comment.shortName }}
              </strong>
              <div
                v-if="comment.sourceWeb && (country === 'pl' || country === 'hu' || country === 'ro')"
                class="col-auto c-p-0"
              >
                <Component
                  :is="getCurrentLocaleFlag(comment.sourceWeb)"
                  width="18"
                  height="18"
                  class="block"
                />
              </div>
            </div>
            <ProductStarsRating
              v-if="!comment.is_answer && comment.starCount"
              :rating="comment.starCount"
              origin="cs"
              :show-rating="false"
              :show-origin="false"
              :mobile-minified="false"
              add-class="c-xl:order-1 order-2 c-xl:c-ml-0 c-ml-2 c-lg:c-mt-0 c-mt-1 c-xl:c-flex-full"
            />
            <span class="col-auto c-flex-shrink-0 c-ml-auto c-xl:order-2 order-1">{{ comment.createdAt }}</span>
          </div>
        </div>
        <div
          v-if="registeredUser && !comment.verifiedComment"
          class="flex c-mb-2 c-items-center"
        >
          <span>{{ t('registered_user') }}</span>
          <span
            v-tooltip="{
              content: t('registered_user_message'),
              theme: 'baseTooltip',
            }"
            class="bg-warning c-p-1 c-rounded-circle c-ml-1"
          >
            <i class="mi-info icon-14" />
          </span>
        </div>
        <span
          v-if="comment.isMerged"
          class="auto-translate c-mb-1 block text-muted c-mt-2"
        >
          <AutoTranslate />
          {{ t('translated_review') }}
        </span>
        <!-- review text -->
        <p class="c-mb-1 c-lg:c-mb-0">
          {{ comment.body }}
        </p>
      </div>
    </div>
  </li>
</template>

<script setup>
import { useI18n } from 'vue-i18n'

import defaultReviewImg from '@/assets/images/default-review-image.png'
import RemoteImage from '@/components/general/BaseRemoteImage.vue'
import ProductStarsRating from '@/components/products/ProductStarsRating.vue'
import VerifiedUserSvg from '@/components/svg/VerifiedUserSvg.vue'
import BirdSkin from '@/components/svg/birds/BirdSkin.vue'
import AutoTranslate from '../svg/AutoTranslate.vue'
import { getCurrentLocaleFlag } from '@/components/svg/flags/languagesFlags'
import { useCountry } from '@/mixins/useCountry'

const { t } = useI18n()
const country = useCountry()

const registeredUser = true

defineProps({
  comment: {
    type: Object,
    default() {
      return {}
    },
  },
})
const skins = [
    'cowboy',
    'wizard',
    'warrior',
    'postman',
    'cook',
    'fitness'
];
</script>

<style lang="scss" scoped>
.c-xl\:c-flex-full {
  flex: 0 0 100%;
  @media (min-width: 1200px) {
    flex: auto;
  }
}
.comment-bubble {
  &:before {
    content: '';
    position: absolute;
    width: 0;
    height: 0;
    border-top: 10px solid transparent;
    border-bottom: 10px solid transparent;
    border-right: 10px solid #f7e7d1;
    right: 100%;
    top: 30px;

    @media screen and (max-width: 991px) {
      border-right: 10px solid #ffffff;
    }
    @media screen and (max-width: 767px) {
      top: 20px;
    }
  }
}
</style>

<i18n>
  {
    "cs": {
    "registered_user": "Registrovaný zákazník",
    "registered_user_message": "Komentář napsal zaregistrovaný zákazník",
    "translated_review": "Automatický překlad",
    "verified_user_message": "Ověřený nákup - zákazník produkt zakoupil na e-shopu Mixit"
},
    "sk": {
    "registered_user": "Registrovaný zákazník",
    "registered_user_message": "Komentár napísal registrovaný zákazník",
    "translated_review": "Automatický preklad",
    "verified_user_message": "Overený nákup - zákazník produkt zakúpil na e-shope Mixit"
},
    "pl": {
    "registered_user": "Zarejestrowany klient",
    "registered_user_message": "Komentarz zarejestrowanego klienta.",
    "translated_review": "Tłumaczenie automatyczne",
    "verified_user_message": "Zweryfikowany zakup - klient zakupił produkt w sklepie internetowym Mixit"
},
    "hu": {
    "registered_user": "Regisztrált ügyfél",
    "registered_user_message": "Ezt a értékelést Mixit regisztrált ügyfél írta.",
    "translated_review": "Automatikus fordítás",
    "verified_user_message": "Ellenőrzött vásárlás - vásárló a Mixit webshopban vásárolta meg ezt a terméket"
},
    "de": {
    "registered_user": "Registrierter Kunde",
    "registered_user_message": "Dieser Kommentar wurde von einem registrierten Kunden verfasst.",
    "translated_review": "Automatische Übersetzung",
    "verified_user_message": "Verifizierter Kauf - Der Kunde hat das Produkt bereits gekauft."
},
    "at": {
    "registered_user": "Registrierter Kunde",
    "registered_user_message": "Dieser Kommentar wurde von einem registrierten Kunden verfasst.",
    "translated_review": "Automatische Übersetzung",
    "verified_user_message": "Verifizierter Kauf - Der Kunde hat das Produkt bereits gekauft."
},
    "ro": {
    "registered_user": "Client înregistrat",
    "registered_user_message": "Comentariu scris de un client înregistrat\n\n\n\n\n",
    "translated_review": "Traducere automată",
    "verified_user_message": "Achiziție verificată - clientul a cumpărat produsul de pe e-shopul Mixit.\n\n\n\n\n\n\n"
},
    "fr": {
    "registered_user": "Client enregistré",
    "registered_user_message": "Commentaire écrit par un client enregistré",
    "translated_review": "Traduction automatique",
    "verified_user_message": "Achat vérifié : le client a acheté le produit sur la e-shop Mixit"
},
    "it": {
    "registered_user": "Cliente registrato",
    "registered_user_message": "Commento scritto da un cliente registrato",
    "translated_review": "Traduzione automatica",
    "verified_user_message": "Acquisto verificato: il cliente ha acquistato il prodotto sull'e-shop Mixit"
}
  }
</i18n>
